import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getParcelsTable } from '../services/api';
import PropertyCardGrid from './PropertyCardGrid';
import errorMessage from '../utils/errorMessage';

export const SORT_OPTIONS = [
  { label: 'Best opportunity', sort: 'opportunity_score', order: 'desc' },
  { label: 'Price: Low → High',  sort: 'listing_price',     order: 'asc'  },
  { label: 'Price: High → Low',  sort: 'listing_price',     order: 'desc' },
  { label: 'Most acres',         sort: 'area_acres',        order: 'desc' },
  { label: 'Fewest acres',       sort: 'area_acres',        order: 'asc'  },
  { label: 'Days: Newest',       sort: 'days_on_market',    order: 'asc'  },
];

// Parse a "sort:order" string back to an index into SORT_OPTIONS.
// Falls back to 0 (best opportunity) for unknown values.
function sortKeyToIdx(key) {
  if (!key) return 0;
  const [sort, order] = key.split(':');
  const idx = SORT_OPTIONS.findIndex(o => o.sort === sort && o.order === order);
  return idx >= 0 ? idx : 0;
}

function MapListPanel({
  bbox,
  stateFilter,
  // Three-state listed filter mirroring MapView and FilterPanel:
  //   true  → listed-only (FilterPanel "Listed only" radio)
  //   false → off-market-only (FilterPanel "Off-market only" radio)
  //   null  → no filter (FilterPanel "All" radio — the default)
  // Older callers passed a bare boolean; keep `true` as the default so
  // standalone embeds continue to show listed-only, but treat `null`
  // as "send nothing" so split-view matches the map's behavior. (#194)
  isListed = true,
  minAcres,
  maxAcres,
  minPrice,
  maxPrice,
  // Server-side filter parity with /parcels/points (#194). These
  // all round-trip through /parcels/table; map and list now apply
  // the exact same filter set.
  minPricePerAcre,
  maxPricePerAcre,
  maxWildfireRisk,
  maxFloodRisk,
  minDom,
  maxDom,
  minElevationChange,
  maxElevationChange,
  // Remaining filter parity (#194 follow-up)
  searchQuery,
  maxMineDist,
  minMinesNearby,
  // #997 alt-energy generation-site proximity + tech.
  maxGenerationDist,
  generationTech,
  // #991 HIFLD transmission-line proximity (distance km + voltage/class/type).
  maxPowerlineDist,
  minPowerlineVoltage,
  powerlineVoltClass,
  powerlineLineType,
  priceChangedRecently,
  priceChangeDays,
  minGoldProb,
  minCopperProb,
  minSilverProb,
  minUraniumProb,
  minLithiumProb,
  minReeProb,
  minNiobiumProb,
  // Encumbrance exclusions (#610). Round-trip to /parcels/table
  // as `exclude_liens` / `exclude_easements` query params.
  excludeLiens = false,
  excludeEasements = false,
  // Distressed-seller filter (#629). True → only parcels with
  // foreclose_code IS NOT NULL.
  distressedOnly = false,
  // Land-only confirmed (#630). True → parcels with
  // total_building_value ≤ $1000.
  landOnlyConfirmed = false,
  // Landlocked exclusion (#641 C1 Phase 5). True → hide parcels with
  // is_landlocked=TRUE in geo_features.
  excludeLandlocked = false,
  // Description-NER booleans (#781 / INIT-description-ner). Each is a
  // tri-state filter the backend implements as EXISTS-joins on
  // listing_media. Default false → omit the query param so the URL
  // doesn't carry `?gated_community_only=false`.
  gatedCommunityOnly = false,
  noHoaOnly = false,
  subdividableOnly = false,
  // Photo-content classifier booleans (#783 / INIT-photo-content). Same
  // tri-state shape as the description-NER toggles above. Backend
  // EXISTS-joins listing_media. Default false → omit the param.
  hasHousePhotoOnly = false,
  hasWaterPhotoOnly = false,
  hasLongViewPhotoOnly = false,
  // Listing-quality predictor floor (#775 / INIT-listing-quality).
  // 0-100; null/0 = no filter. Backend EXISTS-joins listings and
  // excludes parcels without an active scored listing when set.
  minListingQuality = null,
  onParcelHover,
  onFocusOnMap,
  listSort,
  onSortChange,
  viewedIds,
  notViewed,
  // Client-side filter parity with MapView (#194) — these don't
  // round-trip through /parcels/table; they're applied after the
  // fetched page is returned, matching exactly the way SearchPage
  // filters MAP markers for "bookmarks only" / "show hidden" / etc.
  bookmarkedIds,
  downvotedIds,
  bookmarksOnly = false,
  showHidden = false,
}) {
  const [data, setData] = useState({ parcels: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // When listSort + onSortChange are provided (URL-managed), use them.
  // Otherwise fall back to local state for standalone embeds.
  const [localSortIdx, setLocalSortIdx] = useState(0);
  const controlled = listSort != null && onSortChange != null;
  const sortIdx = controlled ? sortKeyToIdx(listSort) : localSortIdx;
  const setSortIdx = controlled
    ? (idx) => onSortChange(`${SORT_OPTIONS[idx].sort}:${SORT_OPTIONS[idx].order}`)
    : setLocalSortIdx;

  const { sort, order } = SORT_OPTIONS[sortIdx];

  const apiParams = useMemo(() => {
    const p = { sort, order, page: 1, per_page: 100 };
    if (isListed === true) p.is_listed = true;
    else if (isListed === false) p.is_listed = false;
    // null → omit is_listed entirely (matches "All" radio on MapView)
    if (bbox) p.bbox = bbox;
    if (stateFilter) p.state = stateFilter;
    if (minAcres != null) p.min_acres = minAcres;
    if (maxAcres != null) p.max_acres = maxAcres;
    if (minPrice != null) p.min_price = minPrice;
    if (maxPrice != null) p.max_price = maxPrice;
    if (minPricePerAcre != null) p.min_price_per_acre = minPricePerAcre;
    if (maxPricePerAcre != null) p.max_price_per_acre = maxPricePerAcre;
    if (maxWildfireRisk != null) p.max_wildfire_risk = maxWildfireRisk;
    if (maxFloodRisk != null) p.max_flood_risk = maxFloodRisk;
    if (minDom != null) p.min_dom = minDom;
    if (maxDom != null) p.max_dom = maxDom;
    if (minElevationChange != null) p.min_elevation_change = minElevationChange;
    if (maxElevationChange != null) p.max_elevation_change = maxElevationChange;
    if (searchQuery) p.q = searchQuery;
    if (maxMineDist != null) p.max_mine_dist = maxMineDist;
    if (minMinesNearby != null) p.min_mines_nearby = minMinesNearby;
    // #997: generation-site proximity + optional tech restriction. Names
    // match the backend `/parcels` contract; tech only with a distance.
    if (maxGenerationDist != null) {
      p.max_dist_generation_km = maxGenerationDist;
      if (generationTech) p.generation_tech = generationTech;
    }
    // #991: transmission-line proximity. Backend `max_powerline_dist` is
    // METRES, so convert the km-valued prop. voltage/class/type require a
    // matching line nearby (default 8 km radius when no distance is set),
    // so they stand alone without a distance cap.
    if (maxPowerlineDist != null) p.max_powerline_dist = maxPowerlineDist * 1000;
    if (minPowerlineVoltage != null) p.min_powerline_voltage = minPowerlineVoltage;
    if (powerlineVoltClass) p.powerline_volt_class = powerlineVoltClass;
    if (powerlineLineType) p.powerline_line_type = powerlineLineType;
    if (priceChangedRecently) {
      p.price_changed_recently = true;
      if (priceChangeDays) p.price_change_days = priceChangeDays;
    }
    if (minGoldProb != null) p.min_gold_prob = minGoldProb;
    if (minCopperProb != null) p.min_copper_prob = minCopperProb;
    if (minSilverProb != null) p.min_silver_prob = minSilverProb;
    if (minUraniumProb != null) p.min_uranium_prob = minUraniumProb;
    if (minLithiumProb != null) p.min_lithium_prob = minLithiumProb;
    if (minReeProb != null) p.min_ree_prob = minReeProb;
    // Niobium (#990) — parity with the six commodities above.
    if (minNiobiumProb != null) p.min_niobium_prob = minNiobiumProb;
    if (excludeLiens) p.exclude_liens = true;
    if (excludeEasements) p.exclude_easements = true;
    if (distressedOnly) p.distressed_only = true;
    if (landOnlyConfirmed) p.land_only_confirmed = true;
    if (excludeLandlocked) p.exclude_landlocked = true;
    // #781: description-NER booleans — backend EXISTS-joins listing_media.
    if (gatedCommunityOnly) p.gated_community_only = true;
    if (noHoaOnly) p.no_hoa_only = true;
    if (subdividableOnly) p.subdividable_only = true;
    // #783: photo-content classifier booleans — backend EXISTS-joins
    // listing_media aggregate columns (has_house_photo / etc.).
    if (hasHousePhotoOnly) p.has_house_photo_only = true;
    if (hasWaterPhotoOnly) p.has_water_photo_only = true;
    if (hasLongViewPhotoOnly) p.has_long_view_photo_only = true;
    // #775: listing-quality floor — backend EXISTS-joins listings.
    if (minListingQuality != null && minListingQuality !== '' && Number(minListingQuality) > 0) {
      p.min_listing_quality = Number(minListingQuality);
    }
    return p;
  }, [bbox, stateFilter, isListed, minAcres, maxAcres, minPrice, maxPrice,
      minPricePerAcre, maxPricePerAcre, maxWildfireRisk, maxFloodRisk,
      minDom, maxDom, minElevationChange, maxElevationChange,
      searchQuery, maxMineDist, minMinesNearby, maxGenerationDist, generationTech,
      maxPowerlineDist, minPowerlineVoltage, powerlineVoltClass, powerlineLineType,
      priceChangedRecently, priceChangeDays,
      minGoldProb, minCopperProb, minSilverProb, minUraniumProb, minLithiumProb, minReeProb, minNiobiumProb,
      excludeLiens, excludeEasements, distressedOnly, landOnlyConfirmed, excludeLandlocked,
      gatedCommunityOnly, noHoaOnly, subdividableOnly,
      hasHousePhotoOnly, hasWaterPhotoOnly, hasLongViewPhotoOnly,
      minListingQuality, sort, order]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getParcelsTable(apiParams)
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e) => {
        if (cancelled) return;
        // Sister-agent c4faf5a returns 503 with this detail when the
        // elevation_range column is unpopulated and a min/max
        // elevation filter is set (tracked in #399). Surface it as an
        // "empty result with a hint" rather than a wall of red error
        // text — the filter is structurally valid, the data just
        // isn't there yet.
        //
        // NOTE: the elevation_range hint check needs the raw detail
        // STRING to regex-match against. Most other detail responses
        // are pydantic arrays of {type,loc,msg,...} that crash React
        // if rendered as a child (see PR-733 / PR-736). So: only treat
        // detail as a string for the regex check; for setError, route
        // through errorMessage() which flattens arrays.
        const rawDetail = e?.response?.data?.detail;
        const detailStr = typeof rawDetail === 'string' ? rawDetail : '';
        if (/elevation_range/i.test(detailStr)) {
          setData({ parcels: [], total: 0 });
          setError('Elevation data isn’t loaded yet — this filter will work after the next backfill. Clear it to see results.');
        } else {
          setError(errorMessage(e, 'Failed to load parcels.'));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiParams]);

  return (
    <div
      className="map-list-panel"
      style={{
        display: 'flex', flexDirection: 'column', height: '100%',
        background: '#fff', borderLeft: '1px solid #e1e4e8',
        minWidth: 360, fontSize: '0.78rem',
      }}
    >
      <div style={{
        padding: '8px 10px', borderBottom: '1px solid #e1e4e8',
        background: '#f8f9fa', display: 'flex', gap: 8,
        alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
      }}>
        <div>
          {loading ? 'Loading…' : (
            error ? (
              // Elevation-data-pending is a known/temporary state, not
              // a server error — render in neutral grey rather than
              // alarm red so users aren't scared by an expected message.
              <span style={{
                color: /elevation/i.test(error) ? '#666' : '#b00020',
                fontStyle: /elevation/i.test(error) ? 'italic' : 'normal',
              }}>{error}</span>
            ) : (
              <>
                <strong>{data.total.toLocaleString()}</strong> in view
                {data.total > 100 && (
                  <span style={{ color: '#888', marginLeft: 4 }}>(showing top 100)</span>
                )}
              </>
            )
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select
            value={sortIdx}
            onChange={(e) => setSortIdx(Number(e.target.value))}
            aria-label="Sort parcels"
            style={{ fontSize: '0.78rem', padding: '2px 4px', border: '1px solid #ccc', borderRadius: 3 }}
          >
            {SORT_OPTIONS.map((o, i) => (
              <option key={i} value={i}>{o.label}</option>
            ))}
          </select>
          <Link
            to={{
              pathname: '/properties',
              search: bbox ? `?${new URLSearchParams({ ...(stateFilter && { state: stateFilter }), sort, order }).toString()}` : '',
            }}
            style={{ color: '#1976d2', fontSize: '0.78rem', textDecoration: 'none', whiteSpace: 'nowrap' }}
            title="Open the full /properties page with these filters"
          >
            Open full list →
          </Link>
        </div>
      </div>

      <div style={{ overflow: 'auto', flex: 1 }}>
        <PropertyCardGrid
          parcels={(() => {
            // Compose the same client-side filter chain that
            // SearchPage applies to map markers (#194). Each filter
            // is a no-op when its precondition isn't met, so the
            // common case (no filters set) returns the unfiltered list.
            let out = data.parcels;
            if (bookmarksOnly && bookmarkedIds?.size > 0) {
              out = out.filter((p) => bookmarkedIds.has(p.parcel_id));
            }
            if (!showHidden && downvotedIds?.size > 0) {
              out = out.filter((p) => !downvotedIds.has(p.parcel_id));
            }
            if (notViewed && viewedIds?.size > 0) {
              out = out.filter((p) => !viewedIds.has(p.parcel_id));
            }
            return out;
          })()}
          loading={loading}
          emptyMessage="No listed parcels in view. Pan or zoom to find some."
          onParcelHover={onParcelHover}
          onFocusOnMap={onFocusOnMap}
          viewedIds={viewedIds}
        />
      </div>
    </div>
  );
}

export default MapListPanel;
